import * as semver from 'semver';
import type { AgentUpdateState } from '@kirin/api-client/contracts/agent-auto-update';

/**
 * KIR-3911: true when `version` is strictly older than `current` (both parseable
 * as semver). Unknown/unparseable inputs are never "outdated" — we don't color
 * on uncertainty.
 */
export function isAgentVersionOutdated(
  version: string | null | undefined,
  current: string | null | undefined
): boolean {
  if (!version || !current) return false;
  const v = semver.valid(semver.coerce(version));
  const c = semver.valid(semver.coerce(current));
  if (!v || !c) return false;
  return semver.lt(v, c);
}

/**
 * KIR-5191: may an outdated agent show "Update available" beside this update
 * state? Not while updating, failed, held or pinned: the state says more than
 * the pill. With updates Off, or on a machine that cannot update by itself,
 * both apply (someone has to update it by hand). `null` = state unknown or
 * automatic updates hidden.
 */
export function showsUpdateAvailableWith(
  updateState: AgentUpdateState | null
): boolean {
  return (
    updateState === null ||
    updateState === 'up_to_date' ||
    updateState === 'off' ||
    updateState === 'not_available'
  );
}

/**
 * KIR-5412: is `version` at or past `latest`? Mirrors the backend's
 * `isVersionAtLeast` (the "Agent up to date" tile): dev builds, unparseable
 * and unknown inputs are never "at least", so nothing reads up to date on
 * uncertainty.
 */
export function isAgentVersionAtLeast(
  version: string | null | undefined,
  latest: string | null | undefined
): boolean {
  const parse = (raw: string | null | undefined): string | null =>
    !raw || raw.trim().toLowerCase().startsWith('dev')
      ? null
      : semver.valid(semver.coerce(raw));
  const v = parse(version);
  const l = parse(latest);
  if (!v || !l) return false;
  return semver.gte(v, l);
}

/**
 * KIR-3911: numeric semver ordering for agent-version sorting (so `3.9.0`
 * sorts before `3.10.0`, matching the backend's numeric least-version logic —
 * a lexical sort would invert them). Both parseable → semver compare; otherwise
 * fall back to a stable lexical compare. Empty/unparseable values sort last.
 */
export function compareAgentVersions(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const va = a ? semver.valid(semver.coerce(a)) : null;
  const vb = b ? semver.valid(semver.coerce(b)) : null;
  if (va && vb) return semver.compare(va, vb);
  // Parseable versions always precede unparseable/empty ones.
  if (va) return -1;
  if (vb) return 1;
  return (a ?? '').localeCompare(b ?? '');
}
